/**
 * The rendered world region: what the camera shows, as opposed to what the
 * simulation is.
 *
 * A 640x360 buffer in a 405x870 window can never be more than 228px tall, and it
 * is already 217 — the field is width-bound and sitting at 95% of its ceiling,
 * so no amount of compacting the chrome around it can make it bigger. The only
 * lever left is what the camera is pointed at. On a window that is not 16:9 this
 * grows the *rendered* slice along whichever axis the fit is wasting, with the
 * 640x360 band centred inside it, and the lattice — which is procedural and has
 * no fixed extent — simply covers more world.
 *
 * Three rules, and the first is the one everything else depends on:
 *
 *  1. **`VIEW` is the simulation and this never touches it.** The band is always
 *     exactly 640x360 in world units, always at world (0,0)-(640,360). `Track`,
 *     `Flock`, the spatial hash, `buildable` and every balance figure are
 *     authored against those numbers and cannot tell that this file exists. A
 *     region is a camera, not a playfield.
 *  2. **The band stays on the pixel grid.** Extents snap to `REGION.quantumPx`,
 *     so the centring offset is always a whole number of buffer pixels and a
 *     sprite that snapped to a pixel before the resize is still on one after it.
 *  3. **The fill is capped.** `REGION.maxAxisMul` is a GPU budget — the scene
 *     draw and every post pass run at region resolution. Past the cap the fit
 *     letterboxes exactly as it did before this existed.
 *
 * Nothing here reads the DOM. `Renderer.resize` measures the host and calls
 * {@link ViewRegionState.set}; consumers that have to rebuild geometry watch
 * {@link ViewRegionState.generation}, which is the same pull-not-push wiring
 * `core/perf.ts` uses for the governor.
 */

import { REGION, VIEW } from './config';

/** A slice of world, in buffer pixels. The band is `(0,0)` to `(640,360)`. */
export interface ViewRegion {
  /** World x of the region's left edge. Zero or negative. */
  x0: number;
  /** World y of the region's top edge. Zero or negative. */
  y0: number;
  width: number;
  height: number;
}

/** Buffer pixels the region covers, over what the bare band covers. */
export function fillScaleOf(r: ViewRegion): number {
  return (r.width * r.height) / (VIEW.width * VIEW.height);
}

/**
 * Quantises *toward* the base, never past the extent asked for.
 *
 * Rounding to nearest would sometimes hand back a region wider than the window
 * can show, and the fit would letterbox the difference — the exact thing this
 * whole file exists to remove. Flooring means the region is at worst one tile
 * short, which is under ten CSS pixels at every viewport in the matrix, and the
 * axis that is *not* extending stays exactly full. It also gives near-16:9
 * windows a deadband: a 2% deviation asks for 653 and gets the bare 640, so the
 * composition the reference frames were tuned against does not move for a
 * scrollbar's worth of aspect.
 */
function snap(v: number): number {
  const q = REGION.quantumPx;
  return Math.floor(v / q) * q;
}

/**
 * The region for a host box, in whatever unit the box is measured in.
 *
 * Only the ratio matters, so CSS pixels and device pixels give the same answer —
 * which is what keeps the region stable across a DPR change that moves no
 * furniture. Exactly one axis ever grows: the fit is decided by the *tighter* of
 * the two ratios, and stretching the axis that is already binding would buy
 * nothing and cost fill.
 */
export function regionFor(hostWidth: number, hostHeight: number): ViewRegion {
  let width: number = VIEW.width;
  let height: number = VIEW.height;

  if (hostWidth > 0 && hostHeight > 0) {
    const host = hostWidth / hostHeight;
    const base = VIEW.width / VIEW.height;
    if (host > base) {
      // Short and wide: spend the surplus on world either side of the band.
      const want = VIEW.height * host;
      width = Math.max(VIEW.width, snap(Math.min(want, VIEW.width * REGION.maxAxisMul)));
    } else if (host < base) {
      const want = VIEW.width / host;
      height = Math.max(VIEW.height, snap(Math.min(want, VIEW.height * REGION.maxAxisMul)));
    }
  }

  return {
    x0: (VIEW.width - width) / 2,
    y0: (VIEW.height - height) / 2,
    width,
    height,
  };
}

/** True when two regions describe the same slice. */
export function sameRegion(a: ViewRegion, b: ViewRegion): boolean {
  return a.x0 === b.x0 && a.y0 === b.y0 && a.width === b.width && a.height === b.height;
}

/** A client-space box, in the shape `getBoundingClientRect` returns. */
export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The band's own box inside a canvas box.
 *
 * The canvas shows the whole region, so anything that wants to place DOM against
 * *game* coordinates — a damage floater, a tile centre, a touch radius in CSS
 * pixels — has to go through this rather than through the canvas rect. When the
 * region is the bare band the two are identical, which is why this was invisible
 * before the region existed.
 */
export function bandBoxIn(canvas: Box, r: ViewRegion = region.current): Box {
  if (!(canvas.width >= 1) || !(canvas.height >= 1)) return canvas;
  const perPixelX = canvas.width / r.width;
  const perPixelY = canvas.height / r.height;
  return {
    left: canvas.left - r.x0 * perPixelX,
    top: canvas.top - r.y0 * perPixelY,
    width: VIEW.width * perPixelX,
    height: VIEW.height * perPixelY,
  };
}

/**
 * The live region. One per page, exactly like `perf`.
 *
 * `current` is mutated in place rather than replaced so a consumer may hold the
 * reference; `generation` is what tells it the contents moved.
 */
export class ViewRegionState {
  /** Read this; never write to it. */
  readonly current: ViewRegion = regionFor(VIEW.width, VIEW.height);

  /** Bumped whenever the slice changes. Consumers compare rather than diff. */
  generation = 1;

  /** Returns true when the region actually moved. */
  set(next: ViewRegion): boolean {
    if (sameRegion(this.current, next)) return false;
    this.current.x0 = next.x0;
    this.current.y0 = next.y0;
    this.current.width = next.width;
    this.current.height = next.height;
    this.generation++;
    return true;
  }

  /** Buffer pixels the region covers, over what the bare band covers. */
  get fillScale(): number {
    return fillScaleOf(this.current);
  }
}

export const region = new ViewRegionState();

// Debug surface, beside `perf.ts`'s `__pixeltdPerf`. `tools/fieldfit.mjs` reads
// the region out of a live page to prove the fit; nothing writes through it.
if (typeof window !== 'undefined') {
  (window as unknown as { __pixeltdRegion?: ViewRegion }).__pixeltdRegion = region.current;
}
